import { Pipe, PipeTransform, inject, NgZone, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import DOMPurify from 'dompurify';
import { marked } from 'marked';
import { OpenSearchService } from '../services/open-search.service';
import { TranslateService } from '@ngx-translate/core';
import { ExportService } from '../services/export.service';
import { Subscription } from 'rxjs';

const renderer = new marked.Renderer();
renderer.link = ({ href, title, text }) => {
  const safeHref = /^https?:\/\//i.test(href ?? '') ? href : '#';
  const safeTitle = title ? ` data-tooltip-title="${title.replace(/"/g, '&quot;')}"` : '';
  let displayUrl = safeHref ?? '';
  try {
    const u = new URL(displayUrl);
    displayUrl = u.origin + u.pathname;
  } catch {
    /* no-op */
  }
  return (
    `<a class="link_response custom-tooltip-trigger"` +
    ` href="${safeHref}"` +
    ` target="_blank"` +
    ` rel="noopener noreferrer"${safeTitle}` +
    ` data-tooltip-url="${displayUrl}"` +
    ` data-tooltip-url-full="${safeHref}"` +
    `>[${text}]</a>`
  );
};
marked.use({ gfm: true });
marked.setOptions({ renderer });

@Pipe({
  name: 'markdownToPlainText',
  pure: false,
})
export class MarkdownToPlainTextPipe implements PipeTransform, OnDestroy {
  private static listenerAttached = false;
  private tooltipService = inject(OpenSearchService);
  private ngZone = inject(NgZone);
  private sanitizer = inject(DomSanitizer);
  private translateService = inject(TranslateService);
  private exportService = inject(ExportService); // ← NEW
  cdr = inject(ChangeDetectorRef);
  private activeTooltip: HTMLElement | null = null;
  private langChangeSubscription: Subscription;
  private lastCacheKey = '';
  private cachedOutput: SafeHtml | string = '';
  private currentLang: string;

  constructor() {
    if (!MarkdownToPlainTextPipe.listenerAttached) {
      this.attachGlobalListeners();
      MarkdownToPlainTextPipe.listenerAttached = true;
    }
    this.currentLang =
      this.translateService.currentLang ?? this.translateService.defaultLang ?? 'es';
    this.langChangeSubscription = this.translateService.onLangChange.subscribe(({ lang }) => {
      this.currentLang = lang;
      this.lastCacheKey = '';
    });
  }

  ngOnDestroy(): void {
    this.langChangeSubscription?.unsubscribe();
  }

  transform(textMarkdown: string, isCopy = false, curatedLessonsMode = false): SafeHtml | string {
    if (!textMarkdown) return '';
    if (isCopy) return this.copyPlainText(textMarkdown);
    const cacheKey = `${textMarkdown}__${this.currentLang}__${curatedLessonsMode}`;
    if (cacheKey === this.lastCacheKey) return this.cachedOutput;
    const { cleanedMarkdown, linkMap } = this.extractLinkDefinitions(textMarkdown);
    const withValidRefs = this.removeUndefinedRefs(cleanedMarkdown, linkMap);
    const preprocessed = this.preprocessMarkdown(withValidRefs);
    const initialMarket = marked(preprocessed);
    if (typeof initialMarket === 'string') {
      let withButtons = this.injectTableDownloadButtons(initialMarket);
      if (curatedLessonsMode) {
        withButtons = this.limitCuratedProjects(withButtons);
      }
      const cleanHtml = DOMPurify.sanitize(withButtons, {
        ADD_ATTR: [
          'target',
          'data-tooltip-url',
          'data-tooltip-url-full',
          'data-tooltip-title',
          'data-table-index',
        ],
        ALLOWED_ATTR: [
          'href',
          'target',
          'rel',
          'data-tooltip-title',
          'data-tooltip-url',
          'data-tooltip-url-full',
          'data-table-index',
          'class',
          'type',
        ],
        ADD_TAGS: ['table', 'thead', 'tbody', 'tr', 'th', 'td', 'button', 'span'],
        FORCE_BODY: true,
      });
      this.lastCacheKey = cacheKey;
      this.cachedOutput = this.sanitizer.bypassSecurityTrustHtml(cleanHtml);
      return this.cachedOutput;
    }
    return '';
  }

  private limitCuratedProjects(html: string): string {
    let listHandled = false;
    const htmlWithOrderedList = html.replace(
      /<ol>([\s\S]*?)<\/ol>/gi,
      (fullList, listContent: string) => {
        if (listHandled) return fullList;
        const itemCount = (listContent.match(/<li\b/gi) ?? []).length;
        if (itemCount <= 3) return fullList;

        listHandled = true;
        const relatedCount = itemCount - 3;
        const label = this.escapeHtml(
          this.translateService.instant('OPEN_SEARCH.RELATED_PROJECTS', { count: relatedCount }),
        );
        return `<div class="curated-project-list">
        <ol>${listContent}</ol>
        <button type="button" class="curated-expand-button" data-curated-expand="projects">
          <span class="curated-expand-label">${label}</span>
          <span class="curated-collapse-label">${this.escapeHtml(this.translateService.instant('OPEN_SEARCH.SHOW_LESS'))}</span>
        </button>
      </div>`;
      },
    );

    if (listHandled) return htmlWithOrderedList;
    const numberedSectionRe = /<h3\b[^>]*>\s*\d+[.)][\s\S]*?(?=<h3\b[^>]*>\s*\d+[.)]|<h[12]\b|$)/gi;
    const sections: Array<{ full: string; index: number }> = [];
    let match: RegExpExecArray | null;
    while ((match = numberedSectionRe.exec(htmlWithOrderedList)) !== null) {
      sections.push({ full: match[0], index: match.index });
    }
    if (sections.length <= 3) return htmlWithOrderedList;

    const start = sections[0].index;
    const end = sections[sections.length - 1].index + sections[sections.length - 1].full.length;
    const relatedCount = sections.length - 3;
    const label = this.escapeHtml(
      this.translateService.instant('OPEN_SEARCH.RELATED_PROJECTS', { count: relatedCount }),
    );
    const projectList = sections
      .map(section => `<div class="curated-project-item">${section.full}</div>`)
      .join('');
    const replacement = `<div class="curated-project-list curated-project-list--numbered">
      ${projectList}
      <button type="button" class="curated-expand-button" data-curated-expand="projects">
        <span class="curated-expand-label">${label}</span>
        <span class="curated-collapse-label">${this.escapeHtml(this.translateService.instant('OPEN_SEARCH.SHOW_LESS'))}</span>
      </button>
    </div>`;
    return htmlWithOrderedList.slice(0, start) + replacement + htmlWithOrderedList.slice(end);
  }
  private extractLinkDefinitions(markdown: string): {
    cleanedMarkdown: string;
    linkMap: Map<string, { url: string; title: string }>;
  } {
    const linkMap = new Map<string, { url: string; title: string }>();
    const linkDefRe = /^\[(\d+)\]:\s*(\S+)(?:\s+"([^"]*)")?[ \t]*$/gm;
    let match: RegExpExecArray | null;
    while ((match = linkDefRe.exec(markdown)) !== null) {
      const num = match[1];
      const url = match[2];
      const title = match[3] ?? '';
      linkMap.set(num, { url, title });
    }
    const cleanedMarkdown = markdown
      .replace(/^\[\d+\]:\s*\S+[^\n]*$/gm, '')
      .replace(/\n{3,}/g, '\n\n');
    return { cleanedMarkdown, linkMap };
  }

  private removeUndefinedRefs(
    markdown: string,
    linkMap?: Map<string, { url: string; title: string }>,
  ): string {
    const definedLinks = linkMap ?? new Map<string, { url: string; title: string }>();
    if (!linkMap) {
      const linkDefRe = /^\[(\d+)\]:/gm;
      let match: RegExpExecArray | null;
      while ((match = linkDefRe.exec(markdown)) !== null) {
        definedLinks.set(match[1], { url: '', title: '' });
      }
    }
    return markdown
      .replace(/\[(\d+)\]/g, (full, num) => {
        if (!definedLinks.has(num)) return '';
        const def = definedLinks.get(num)!;
        if (!def.url) return full;
        const titlePart = def.title ? ` "${def.title}"` : '';
        return `[${num}](${def.url}${titlePart})`;
      })
      .replace(/ {2,}/g, ' ');
  }

  private escapeHtml(s: string): string {
    return s
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#x27;');
  }

  private injectTableDownloadButtons(html: string): string {
    let tableIndex = 0;
    const label = this.escapeHtml(this.translateService.instant('OPEN_SEARCH.DOWNLOAD_EXCEL'));
    return html.replace(/<table[\s\S]*?<\/table>/gi, tableHtml => {
      const idx = tableIndex++;
      return `
        <div class="table-export-wrapper">
          ${tableHtml}
          <div class="table-export-bar">
            <button
              data-fs-element="Open-Search-Table-Button-DownloadExcel"
              type="button"
              class="table-download-btn"
              data-table-index="${idx}">
              <span class="table-download-label">${label}</span>
            </button>
          </div>
        </div>`;
    });
  }
  private attachGlobalListeners(): void {
    if (typeof document === 'undefined') return;
    document.addEventListener(
      'click',
      (event: MouseEvent) => {
        const target = event.target as HTMLElement;
        const expandButton = target.closest('.curated-expand-button') as HTMLElement | null;
        if (expandButton) {
          event.preventDefault();
          const projectList = expandButton.closest('.curated-project-list');
          projectList?.classList.toggle('curated-project-list--expanded');
          return;
        }
        const btn = target.closest('.table-download-btn') as HTMLElement | null;
        if (btn && btn.closest('.markdown-body')) {
          event.preventDefault();
          event.stopPropagation();
          const idx = parseInt(btn.getAttribute('data-table-index') ?? '', 10);
          if (!isNaN(idx) && idx >= 0) {
            this.ngZone.runOutsideAngular(() => this.exportService.exportTableToExcel(idx));
          }
        }
      },
      true,
    );
    document.addEventListener(
      'mouseover',
      (event: MouseEvent) => {
        const target = event.target as HTMLElement;
        const tooltipElement = target.closest('.custom-tooltip-trigger') as HTMLElement;
        if (tooltipElement) {
          const displayUrl =
            tooltipElement.getAttribute('data-tooltip-url') ||
            tooltipElement.getAttribute('href') ||
            '';
          const fullUrl =
            tooltipElement.getAttribute('data-tooltip-url-full') ||
            tooltipElement.getAttribute('href') ||
            '';
          const tooltipContent = tooltipElement.getAttribute('data-tooltip-title') || displayUrl;
          const isValidUrl = /^https?:\/\//i.test(fullUrl);
          this.ngZone.run(() => {
            if (isValidUrl) {
              this.tooltipService.setActiveTooltip({
                url: fullUrl,
                tooltipClass: 'tooltip-no-wrap',
              });
            } else {
              this.tooltipService.clearActiveTooltip();
            }
          });
          this.showCustomTooltip(tooltipElement, tooltipContent);
        }
      },
      true,
    );
    document.addEventListener(
      'mouseout',
      (event: MouseEvent) => {
        const target = event.target as HTMLElement;
        const tooltipElement = target.closest('.custom-tooltip-trigger');
        if (tooltipElement) {
          this.ngZone.run(() => this.tooltipService.clearActiveTooltip());
          this.hideCustomTooltip();
        }
      },
      true,
    );
  }

  private showCustomTooltip(element: HTMLElement, content: string): void {
    this.hideCustomTooltip();
    const tooltip = document.createElement('div');
    tooltip.className = 'custom-tooltip tooltip-no-wrap';
    tooltip.textContent = content;
    tooltip.style.cssText = 'position:absolute;z-index:10000;opacity:0;';
    document.body.appendChild(tooltip);
    this.activeTooltip = tooltip;

    requestAnimationFrame(() => {
      const rect = element.getBoundingClientRect();
      const tooltipRect = tooltip.getBoundingClientRect();
      let top = rect.bottom + window.scrollY + 8;
      let left = rect.left + window.scrollX + rect.width / 2 - tooltipRect.width / 2;
      if (left + tooltipRect.width > window.innerWidth)
        left = window.innerWidth - tooltipRect.width - 10;
      if (left < 0) left = 10;
      if (top + tooltipRect.height > window.innerHeight + window.scrollY)
        top = rect.top + window.scrollY - tooltipRect.height - 8;
      tooltip.style.top = `${top}px`;
      tooltip.style.left = `${left}px`;
      tooltip.style.opacity = '1';
    });
  }

  private hideCustomTooltip(): void {
    if (this.activeTooltip) {
      this.activeTooltip.remove();
      this.activeTooltip = null;
    }
  }

  copyPlainText(markdown: string): string {
    const withTablesConverted = this.markdownTableToPlainText(markdown);
    return withTablesConverted
      .replace(/^#### (.*$)/gim, '$1')
      .replace(/^### (.*$)/gim, '$1')
      .replace(/^## (.*$)/gim, '$1')
      .replace(/^# (.*$)/gim, '$1')
      .replace(/^- (.*$)/gim, '• $1')
      .replace(/\*\*(.*?)\*\*/gim, '$1')
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
      .replace(/^\[(\d+)\]:.*$/gm, '')
      .trim();
  }

  private markdownTableToPlainText(markdown: string): string {
    return markdown.replace(/((?:\|.+\|[ \t]*\n?)+)/g, tableBlock => {
      const lines = tableBlock
        .trim()
        .split('\n')
        .filter(l => l.trim());
      const dataLines = lines.filter(l => !/^\|[\s\-|:]+\|$/.test(l.trim()));
      if (dataLines.length === 0) return tableBlock;
      const rows = dataLines.map(l =>
        l
          .split('|')
          .slice(1, -1)
          .map(c => c.trim()),
      );
      const colCount = Math.max(...rows.map(r => r.length));
      const colWidths = Array.from({ length: colCount }, (_, ci) =>
        Math.max(...rows.map(row => (row[ci] ?? '').length)),
      );
      const textRows = rows.map(row =>
        row.map((cell, ci) => cell.padEnd(colWidths[ci])).join('   '),
      );
      const separator = colWidths.map(w => '-'.repeat(w)).join('   ');
      textRows.splice(1, 0, separator);
      return textRows.join('\n') + '\n\n';
    });
  }

  private preprocessMarkdown(markdown: string): string {
    const lines = markdown.split('\n');
    const result: string[] = [];
    const mainItemRe = /^(\d+\.\s+)\*\*/;
    const subItemNumberedRe = /^\d+\.\s+[^*\n]/;
    const subItemBulletRe = /^[-*]\s+/;
    let i = 0;
    while (i < lines.length) {
      const line = lines[i];
      const trimmed = line.trim();
      const mainMatch = mainItemRe.exec(trimmed);
      if (mainMatch) {
        result.push(line);
        const indent = ' '.repeat(mainMatch[1].length);
        i++;
        while (i < lines.length && lines[i].trim() === '') i++;
        while (i < lines.length) {
          const subTrimmed = lines[i].trim();
          if (subItemBulletRe.test(subTrimmed)) {
            result.push(indent + subTrimmed);
            i++;
            while (i < lines.length && lines[i].trim() === '') i++;
          } else if (subItemNumberedRe.test(subTrimmed)) {
            const text = subTrimmed.replace(/^\d+\.\s+/, '');
            result.push(`${indent}- ${text}`);
            i++;
            while (i < lines.length && lines[i].trim() === '') i++;
          } else {
            break;
          }
        }
        result.push('');
      } else {
        result.push(line);
        i++;
      }
    }
    return result.join('\n');
  }
}
